import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { employerAPI, paymentsAPI, accountAPI, notificationsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { 
  Building, Mail, Lock, Bell, CreditCard, Users, Loader2, 
  ExternalLink, Trash2, Check, AlertTriangle, Pause, X
} from 'lucide-react';

const Settings = () => {
  const { logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [notifications, setNotifications] = useState({
    record_acknowledged: true,
    flag_raised: true,
    new_reply: true,
  });
  const [weeklyDigestEnabled, setWeeklyDigestEnabled] = useState(true);
  const [savingDigest, setSavingDigest] = useState(false);
  const [teamEmail, setTeamEmail] = useState('');
  
  // Delete account modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteStep, setDeleteStep] = useState(1);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [pausing, setPausing] = useState(false);

  useEffect(() => {
    fetchData();
    fetchNotificationSettings();
  }, []);

  const fetchNotificationSettings = async () => {
    try {
      const res = await notificationsAPI.getSettings();
      setWeeklyDigestEnabled(res.data.weekly_digest_enabled !== false);
    } catch (error) {
      console.error('Error fetching notification settings:', error);
    }
  };

  const handleToggleWeeklyDigest = async (enabled) => {
    setSavingDigest(true);
    try {
      await notificationsAPI.updateSettings({ weekly_digest_enabled: enabled });
      setWeeklyDigestEnabled(enabled);
      toast.success(enabled ? 'Weekly digest enabled' : 'Weekly digest disabled');
    } catch (error) {
      toast.error('Failed to update notification settings');
    } finally {
      setSavingDigest(false);
    }
  };

  const fetchData = async () => {
    try {
      const [profileRes, subRes] = await Promise.all([
        employerAPI.getProfile(),
        paymentsAPI.getSubscriptionInfo()
      ]);
      setProfile(profileRes.data);
      setSubscriptionInfo(subRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (planId) => {
    try {
      const res = await paymentsAPI.createCheckout(planId, window.location.origin);
      window.location.href = res.data.url;
    } catch (error) {
      toast.error('Failed to start checkout');
    }
  };

  const handleManageBilling = async () => {
    try {
      const res = await paymentsAPI.getBillingPortal();
      window.location.href = res.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to open billing portal');
    }
  };

  const handlePauseAccount = async () => {
    setPausing(true);
    try {
      await accountAPI.pauseAccount();
      toast.success('Account paused. You can reactivate by logging in again.');
      setShowDeleteModal(false);
      logout();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to pause account');
    } finally {
      setPausing(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    
    setDeleting(true);
    try {
      await accountAPI.deleteAccount(deleteConfirmText);
      toast.success('Account permanently deleted');
      setShowDeleteModal(false);
      logout();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    } finally {
      setDeleting(false);
    }
  };

  const openDeleteModal = () => {
    setShowDeleteModal(true);
    setDeleteStep(1);
    setDeleteConfirmText('');
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#1B5AFF]" />
        </div>
      </DashboardLayout>
    );
  }

  const isPaid = profile?.subscription_tier && profile.subscription_tier !== 'free' && profile.subscription_tier !== 'trial';
  const isGrowth = profile?.subscription_tier === 'growth';
  const sub = subscriptionInfo?.subscription;

  // Format price for display
  const formatPrice = () => {
    if (sub?.price_amount) {
      return `$${sub.price_amount}/${sub.interval}`;
    }
    // Fallback to plan defaults
    if (profile?.subscription_tier === 'starter') return '$299/month';
    if (profile?.subscription_tier === 'growth') return '$799/month';
    if (profile?.subscription_tier === 'scale') return '$1,999/month';
    return 'Free';
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl space-y-6" data-testid="employer-settings">
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0A0F1E]">Settings</h1>
          <p className="mt-1 text-[#5B6472] text-sm">Manage your account and billing.</p>
        </div>

        {/* Billing */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-4 sm:p-6">
          <div className="flex items-center gap-3 mb-4 sm:mb-6">
            <CreditCard className="w-5 h-5 text-[#1B5AFF]" />
            <h2 className="text-lg font-semibold text-[#0A0F1E]">Billing & Subscription</h2>
          </div>
          
          {isPaid ? (
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-[#F5F8FF]/50 border border-[#E8EDF5]">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <p className="text-[#0A0F1E] font-medium capitalize">{profile.subscription_tier} Plan</p>
                    <p className="text-[#1B5AFF] text-lg font-semibold">{formatPrice()}</p>
                    {sub?.cancel_at_period_end && (
                      <p className="text-amber-400 text-sm mt-1">Cancels at period end</p>
                    )}
                  </div>
                  <span className="px-3 py-1 rounded-full bg-green-500/20 text-green-400 text-sm self-start">
                    Active
                  </span>
                </div>
              </div>
              {sub?.current_period_end && (
                <p className="text-[#5B6472] text-sm">
                  Next billing: {new Date(sub.current_period_end * 1000).toLocaleDateString()}
                </p>
              )}
              <div className="flex flex-wrap gap-3">
                {profile.subscription_tier === 'starter' && (
                  <Button
                    onClick={() => handleUpgrade('growth')}
                    className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl"
                  >
                    Upgrade to Growth
                  </Button>
                )}
                <Button 
                  variant="outline" 
                  onClick={handleManageBilling}
                  className="border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#F5F8FF] rounded-xl"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Manage Billing
                </Button>
              </div>
              <p className="text-[#5B6472] text-xs">
                Update payment method, view invoices, or cancel subscription via Stripe.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-[#5B6472] text-sm">
                {profile?.trial_active 
                  ? `You're on a 3-day trial. Subscribe to keep access after trial ends.`
                  : `Your trial has ended. Subscribe to regain access.`
                }
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Card className="bg-[#F5F8FF]/50 border border-[#E8EDF5] rounded-xl p-4">
                  <h3 className="font-medium text-[#0A0F1E] mb-2">Starter</h3>
                  <p className="text-2xl font-bold text-[#0A0F1E] mb-3">$299<span className="text-sm text-[#5B6472]">/mo</span></p>
                  <ul className="text-[#5B6472] text-sm space-y-1 mb-4">
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400 flex-shrink-0" /> Up to 20 records</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400 flex-shrink-0" /> 50 talent searches/mo</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400 flex-shrink-0" /> Candidate pipeline</li>
                  </ul>
                  <Button onClick={() => handleUpgrade('starter')} variant="outline" className="w-full border-[#1B5AFF]/30 text-[#1B5AFF] hover:bg-[#1B5AFF]/10 rounded-xl">
                    Choose Starter
                  </Button>
                </Card>
                <Card className="bg-[#F5F8FF]/50 border-2 border-[#1B5AFF] rounded-xl p-4">
                  <h3 className="font-medium text-[#1B5AFF] mb-2">Growth</h3>
                  <p className="text-2xl font-bold text-[#0A0F1E] mb-3">$799<span className="text-sm text-[#5B6472]">/mo</span></p>
                  <ul className="text-[#5B6472] text-sm space-y-1 mb-4">
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400 flex-shrink-0" /> Up to 100 records</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400 flex-shrink-0" /> Unlimited searches</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400 flex-shrink-0" /> Full analytics</li>
                  </ul>
                  <Button onClick={() => handleUpgrade('growth')} className="w-full bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl">
                    Choose Growth
                  </Button>
                </Card>
              </div>
            </div>
          )}
        </Card>

        {/* Company Profile Link */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-4 sm:p-6">
          <div className="flex items-center gap-3 mb-4">
            <Building className="w-5 h-5 text-[#1B5AFF]" />
            <h2 className="text-lg font-semibold text-[#0A0F1E]">Company Profile</h2>
          </div>
          <p className="text-[#5B6472] text-sm mb-4">
            Update your company information that candidates see.
          </p>
          <Button variant="outline" className="border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#F5F8FF] rounded-xl" asChild>
            <a href="/employer/profile">Edit Company Profile</a>
          </Button>
        </Card>

        {/* Notifications */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-4 sm:p-6" data-testid="notifications-card">
          <div className="flex items-center gap-3 mb-4 sm:mb-6">
            <Bell className="w-5 h-5 text-[#1B5AFF]" />
            <h2 className="text-lg font-semibold text-[#0A0F1E]">Email Notifications</h2>
          </div>
          <div className="space-y-4">
            {/* Weekly Digest Toggle */}
            <div className="flex items-center justify-between py-2 border-b border-[#E8EDF5] pb-4 mb-4">
              <div>
                <span className="text-[#0A0F1E] font-medium">Weekly Summary Digest</span>
                <p className="text-[#5B6472] text-sm mt-1">
                  Receive a weekly email with pending items, overdue reviews, and pipeline activity
                </p>
              </div>
              <Switch
                checked={weeklyDigestEnabled}
                onCheckedChange={handleToggleWeeklyDigest}
                disabled={savingDigest}
                data-testid="weekly-digest-toggle"
              />
            </div>
            
            {[
              { key: 'record_acknowledged', label: 'When a record is acknowledged' },
              { key: 'flag_raised', label: 'When an employee flags a record' },
              { key: 'new_reply', label: 'When I receive a reply' },
            ].map((item) => (
              <div key={item.key} className="flex items-center justify-between py-2">
                <span className="text-[#0A0F1E] text-sm">{item.label}</span>
                <Switch
                  checked={notifications[item.key]}
                  onCheckedChange={(checked) => setNotifications(prev => ({ ...prev, [item.key]: checked }))}
                />
              </div>
            ))}
          </div>
        </Card>

        {/* Team Members (Growth only) */}
        {isGrowth && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-4 sm:p-6">
            <div className="flex items-center gap-3 mb-4">
              <Users className="w-5 h-5 text-[#1B5AFF]" />
              <h2 className="text-lg font-semibold text-[#0A0F1E]">Team Members</h2>
            </div>
            <p className="text-[#5B6472] text-sm mb-4">
              Invite colleagues to access your employer account.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                value={teamEmail}
                onChange={(e) => setTeamEmail(e.target.value)}
                placeholder="colleague@company.com"
                className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-11 flex-1"
              />
              <Button className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl">
                Invite
              </Button>
            </div>
          </Card>
        )}

        {/* Delete Account */}
        <Card className="bg-[#FFFFFF] border border-red-500/20 rounded-xl p-4 sm:p-6">
          <div className="flex items-center gap-3 mb-4">
            <Trash2 className="w-5 h-5 text-red-400" />
            <h2 className="text-lg font-semibold text-red-400">Account Management</h2>
          </div>
          <p className="text-[#5B6472] text-sm mb-4">
            Pause your account to temporarily disable it, or permanently delete it.
          </p>
          <Button 
            variant="outline" 
            onClick={openDeleteModal}
            className="border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-xl"
          >
            Manage Account
          </Button>
        </Card>
      </div>

      {/* Delete Account Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="bg-[#FFFFFF] border-[#E8EDF5] text-[#0A0F1E] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              Account Management
            </DialogTitle>
          </DialogHeader>

          {deleteStep === 1 && (
            <div className="space-y-4 py-4">
              <p className="text-[#5B6472]">What would you like to do?</p>
              
              <button
                onClick={handlePauseAccount}
                disabled={pausing}
                className="w-full p-4 rounded-xl bg-[#F5F8FF]/50 border border-[#E8EDF5] hover:border-amber-500/30 transition-colors text-left"
              >
                <div className="flex items-center gap-3">
                  <Pause className="w-5 h-5 text-amber-400" />
                  <div>
                    <p className="text-[#0A0F1E] font-medium">Pause Account</p>
                    <p className="text-[#5B6472] text-sm">Freeze your profile and subscription. Reactivate anytime by logging in.</p>
                  </div>
                </div>
              </button>
              
              <button
                onClick={() => setDeleteStep(2)}
                className="w-full p-4 rounded-xl bg-[#F5F8FF]/50 border border-[#E8EDF5] hover:border-red-500/30 transition-colors text-left"
              >
                <div className="flex items-center gap-3">
                  <Trash2 className="w-5 h-5 text-red-400" />
                  <div>
                    <p className="text-red-400 font-medium">Delete Permanently</p>
                    <p className="text-[#5B6472] text-sm">This cannot be undone. All data will be anonymized.</p>
                  </div>
                </div>
              </button>
            </div>
          )}

          {deleteStep === 2 && (
            <div className="space-y-4 py-4">
              <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30">
                <p className="text-red-400 text-sm">
                  <strong>Warning:</strong> This action cannot be undone. Your account will be permanently deleted and your personal data anonymized.
                </p>
              </div>
              
              <div className="space-y-2">
                <p className="text-[#0A0F1E] text-sm">What happens:</p>
                <ul className="text-[#5B6472] text-sm space-y-1 ml-4 list-disc">
                  <li>Your Stripe subscription will be cancelled immediately</li>
                  <li>Your profile data will be anonymized (GDPR/CCPA compliant)</li>
                  <li>VETT Records you created will remain (co-signed documents)</li>
                  <li>You'll receive a confirmation email</li>
                </ul>
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#0A0F1E] text-sm">Type DELETE to confirm</Label>
                <Input
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
                  placeholder="DELETE"
                  className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-11"
                />
              </div>
              
              <DialogFooter className="gap-2">
                <Button
                  variant="outline"
                  onClick={() => setDeleteStep(1)}
                  className="border-[#E8EDF5] text-[#0A0F1E]"
                >
                  Back
                </Button>
                <Button
                  onClick={handleDeleteAccount}
                  disabled={deleting || deleteConfirmText !== 'DELETE'}
                  className="bg-red-500 hover:bg-red-600 hover:bg-red-700 text-[#FFFFFF] rounded-xl"
                >
                  {deleting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    'Delete Permanently'
                  )}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default Settings;
